import { EditOutlined, GroupOutlined, InfoOutlined } from '@mui/icons-material'
import {
  Button,
  Checkbox,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import type { SupplierRow, SupplierTab } from './SupplierList.types'
import { useUser } from 'src/hooks'
import { useMemo } from 'react'
import './Supplier.css'

const COLUMN_WIDTHS = {
  name: undefined,
  type: '20%',
  blanket: '16%',
  documents: '18%',
  authorizedOem: '14%',
  notes: '10%',
  approve: '12%',
}

export interface SupplierTableProps {
  rows: SupplierRow[]
  activeTab: SupplierTab
  loading: boolean
  pageSize: number
  onEditSupplier: (id: string) => void
  onOpenAuthorizedOem: (rowId: string) => void
  onOpenNotes: (rowId: string) => void
  onApprove: (row: SupplierRow) => void
}

export function SupplierTable({
  rows,
  activeTab,
  loading,
  pageSize,
  onEditSupplier,
  onOpenAuthorizedOem,
  onOpenNotes,
  onApprove,
}: SupplierTableProps) {
  const user = useUser()
  const canApprove = useMemo(() => user.role.admin || user.role.operations, [user.role])

  return (
    <Table stickyHeader className="supplier-table">
      <TableHead>
        <TableRow>
          <TableCell width={COLUMN_WIDTHS.name}>Name</TableCell>
          <TableCell width={COLUMN_WIDTHS.type}>Type</TableCell>
          <TableCell width={COLUMN_WIDTHS.blanket}>
            <div className="blanket-header">
              Blanket{' '}
              <Tooltip title="If checked, this supplier is authorized to sell from all OEMs.">
                <InfoOutlined fontSize="small" />
              </Tooltip>
            </div>
          </TableCell>
          <TableCell width={COLUMN_WIDTHS.documents}>Documents</TableCell>
          <TableCell width={COLUMN_WIDTHS.authorizedOem}>Authorized OEM</TableCell>
          <TableCell width={COLUMN_WIDTHS.notes} align="right">
            Notes
          </TableCell>
          {activeTab === 'pending' && canApprove && <TableCell width={COLUMN_WIDTHS.approve} align="right"></TableCell>}
        </TableRow>
      </TableHead>

      <TableBody>
        {loading
          ? Array.from({ length: pageSize }).map((_, index) => (
              <TableRow key={index}>
                {Array.from({
                  length: Object.keys(COLUMN_WIDTHS).length - (activeTab === 'approved' || !canApprove ? 1 : 0),
                }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : rows.map((row: SupplierRow) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link component="button" onClick={() => onEditSupplier(row.id)}>
                    {row.name}
                  </Link>
                </TableCell>

                <TableCell>{row.type}</TableCell>

                <TableCell>
                  <Checkbox checked={row.blanket} disabled displayOnly />
                </TableCell>

                <TableCell>
                  <Link href={row.documentsUrl} target="_blank" rel="noopener noreferrer">
                    {row.documentsLabel}
                  </Link>
                </TableCell>

                <TableCell>
                  <IconButton aria-label="Open authorized OEM" onClick={() => onOpenAuthorizedOem(row.id)}>
                    <GroupOutlined />
                  </IconButton>
                </TableCell>

                <TableCell align="right">
                  <IconButton aria-label="Open notes" onClick={() => onOpenNotes(row.id)}>
                    <EditOutlined />
                  </IconButton>
                </TableCell>

                {activeTab === 'pending' && canApprove && (
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        onApprove(row)
                      }}
                    >
                      APPROVE
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
      </TableBody>
    </Table>
  )
}
